<template>
  <div class="flex flex-col">
    <!-- Mobile scrubber (above everything) -->
    <div v-if="isMobile" class="mb-[10px]">
      <TimelineScrubber
        :frames="infringing.frames"
        :matches="scrubberMatches"
        :duration="infringing.duration"
        :content-width="mobileContentWidth"
        :viewport-width="viewportWidth"
        :scroll-left="scrollLeft"
        @update:scroll-left="onScrubberScroll"
      />
    </div>

    <!-- Infringing file header (top) -->
    <TimelineFileHeader
      :label="infringing.label ?? 'Potential infringing work'"
      :title="infringing.title"
      :icon="infringing.icon"
      class="mb-[10px]"
    />

    <!-- Scroll container (mobile scrolls, desktop is fluid) -->
    <div
      ref="scrollRef"
      class="flex flex-col"
      :class="{ 'overflow-x-auto no-scrollbar -mx-5 tablet-h:mx-0': isMobile }"
      @scroll="onScroll"
    >
      <div
        class="flex flex-col"
        :style="isMobile ? { width: `${mobileContentWidth}px` } : undefined"
      >
        <TimelineTrack
          :duration="infringing.duration"
          :audio-url="infringing.audioUrl"
          :transcript="infringing.transcript"
          :frames="infringing.frames"
          :matches="matches"
          side="infringing"
        />

        <div class="mt-[10px]">
          <MatchConnector
            :matches="matches"
            :infringing-duration="infringing.duration"
            :registered-duration="registered.duration"
          />
        </div>

        <div class="mt-[10px]">
          <TimelineTrack
            :duration="registered.duration"
            :audio-url="registered.audioUrl"
            :transcript="registered.transcript"
            :frames="registered.frames"
            :matches="matches"
            side="registered"
          />
        </div>
      </div>
    </div>

    <!-- Registered file footer (bottom) -->
    <TimelineFileHeader
      :title="registered.title"
      :icon="registered.icon"
      :label="registered.label ?? 'Registration'"
      label-position="below"
      class="mt-[10px]"
    />

    <Teleport to="body">
      <div
        v-if="hoveredMatch && tooltipStyle"
        :style="tooltipStyle"
        class="pointer-events-auto"
        @pointerenter="cancelHide"
        @pointerleave="hide"
      >
        <MatchTooltip :match="hoveredMatch" @view="onView" />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import type { FileType, ScoreLevel } from '@/shared/graphql/router/graphql'
import {
  computed,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
  nextTick,
} from 'vue'
import TimelineTrack from './TimelineTrack.vue'
import MatchConnector from './MatchConnector.vue'
import TimelineFileHeader from './TimelineFileHeader.vue'
import TimelineScrubber from './TimelineScrubber.vue'
import MatchTooltip from './MatchTooltip.vue'
import { useDevice } from '@/shared/composables/useDevice'
import {
  TimelineHoverKey,
  type TimelineAnchor,
  type TimelineMatch,
  type TimelineSideMatch,
} from './timeline-types'

export interface TimelineSideData {
  duration: number
  audioUrl?: string | null
  transcript?: readonly string[]
  frames?: readonly string[]
  label?: string
  title?: string
  icon?: { type: FileType; imageUrl?: string | null }
}

const props = defineProps<{
  infringing: TimelineSideData
  registered: TimelineSideData
  matches: readonly TimelineMatch[]
}>()

const emit = defineEmits<{
  view: [matchId: string]
}>()

const device = useDevice()
const isMobile = computed(() => device.isMobile.value)

const hoveredMatch = ref<TimelineMatch | null>(null)
const hoveredAnchor = ref<TimelineAnchor | null>(null)
const hoveredId = computed(() => hoveredMatch.value?.id ?? null)

let pendingHide = false

const enter = (match: TimelineMatch, anchor: TimelineAnchor) => {
  pendingHide = false
  hoveredMatch.value = match
  hoveredAnchor.value = anchor
}

const hide = () => {
  pendingHide = true
  requestAnimationFrame(() => {
    if (!pendingHide) return
    pendingHide = false
    hoveredMatch.value = null
    hoveredAnchor.value = null
  })
}

const cancelHide = () => {
  pendingHide = false
}

provide(TimelineHoverKey, {
  hoveredId,
  hoveredMatch,
  hoveredAnchor,
  enter,
  hide,
})

const TOOLTIP_W = 190
const TOOLTIP_H = 128
const TOOLTIP_GAP = 10

const tooltipStyle = computed(() => {
  const a = hoveredAnchor.value
  if (!a) return null
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768
  const padding = 8
  const totalH = TOOLTIP_H + TOOLTIP_GAP

  let above = true
  let top = a.top - totalH
  if (top < padding) {
    above = false
    top = a.bottom
  }
  let left = a.x - TOOLTIP_W / 2
  left = Math.max(padding, Math.min(vw - TOOLTIP_W - padding, left))
  top = Math.max(padding, Math.min(vh - totalH - padding, top))

  return {
    position: 'fixed' as const,
    left: `${left}px`,
    top: `${top}px`,
    width: `${TOOLTIP_W}px`,
    height: `${totalH}px`,
    paddingTop: above ? '0px' : `${TOOLTIP_GAP}px`,
    paddingBottom: above ? `${TOOLTIP_GAP}px` : '0px',
    boxSizing: 'border-box' as const,
    zIndex: 60,
  }
})

const onView = () => {
  const m = hoveredMatch.value
  if (!m) return
  const el = document.getElementById(`match-${m.id}`)
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  emit('view', m.id)
  hide()
}

const MOBILE_CONTENT_WIDTH = 1200
const mobileContentWidth = MOBILE_CONTENT_WIDTH

const scrollRef = ref<HTMLElement>()
const scrollLeft = ref(0)
const viewportWidth = ref(0)

let ro: ResizeObserver | null = null
let syncing = false

const onScroll = () => {
  const el = scrollRef.value
  if (!el || syncing) return
  scrollLeft.value = el.scrollLeft
  hide()
}

const onScrubberScroll = (v: number) => {
  const el = scrollRef.value
  if (!el) return
  syncing = true
  el.scrollLeft = v
  scrollLeft.value = v
  requestAnimationFrame(() => {
    syncing = false
  })
}

const scrubberMatches = computed<TimelineSideMatch[]>(() =>
  props.matches.map((m) => ({
    id: m.id,
    start: m.infringing.start,
    end: m.infringing.end,
    level: m.level as ScoreLevel,
    match: m,
  }))
)

onMounted(async () => {
  await nextTick()
  const el = scrollRef.value
  if (!el) return
  viewportWidth.value = el.getBoundingClientRect().width
  ro = new ResizeObserver(([entry]) => {
    viewportWidth.value = entry.contentRect.width
  })
  ro.observe(el)
})

onUnmounted(() => {
  ro?.disconnect()
})

watch(isMobile, (v) => {
  if (!v && scrollRef.value) {
    scrollRef.value.scrollLeft = 0
    scrollLeft.value = 0
  }
})
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  scrollbar-width: none;
}
</style>
